import { MutableRefObject, RefObject, useEffect, useState } from "react";
import { IOptionMarket } from "../../contexts/MarketInstrumentContext/useGetMarkets";
import useScroll from "../../hooks/useScroll";
import { computeFilteredRows } from "./helpers";
import { ITableContentRowWithCallPut } from "./OptionsTable";

interface IUseOptionsChainScrollJackingParams {
  expiry: number | undefined;
  selectedOptionInstrument: IOptionMarket | undefined;
  scrollableRef: RefObject<HTMLDivElement>;
  currentSpotRef: RefObject<HTMLDivElement>;
  lowerThanSpotRowRefs: MutableRefObject<HTMLDivElement[]>;
  higherThanSpotRowRefs: MutableRefObject<HTMLDivElement[]>;
  filteredRows: ReturnType<typeof computeFilteredRows>;
  currentIndexPrice: string | undefined;
}

export function useOptionsChainScrollJacking({
  expiry,
  selectedOptionInstrument,
  scrollableRef,
  currentSpotRef,
  lowerThanSpotRowRefs,
  higherThanSpotRowRefs,
  filteredRows,
  currentIndexPrice,
}: IUseOptionsChainScrollJackingParams) {
  const { scrollVerticalTo } = useScroll(scrollableRef.current);
  const [scrolledToInstrumentName, setScrolledToInstrumentName] =
    useState<string>();

  // ================================
  // ======== SCROLL JACKING ========
  // ================================
  // Whenever expiry changes, scrolls to strike (if no current instrument is selected)
  // also updates whenever no of rows changes
  useEffect(() => {
    if (
      expiry &&
      !selectedOptionInstrument?.instrument_name &&
      scrollableRef.current &&
      currentSpotRef.current
    ) {
      const halfScrollContainer = scrollableRef.current.offsetHeight / 2;
      const halfCurrentSpotHeight = currentSpotRef.current.offsetHeight / 2;
      scrollableRef.current.scrollTo({
        top:
          currentSpotRef.current.offsetTop -
          halfScrollContainer +
          halfCurrentSpotHeight,
        behavior: "smooth",
      });
    }
  }, [
    expiry,
    filteredRows.lessThanCurrPriceRows.length,
    filteredRows.moreThanCurrPriceRows.length,
    selectedOptionInstrument?.instrument_name,
  ]);

  // Whenever selected instrument changes, scrolls to it
  useEffect(() => {
    if (
      selectedOptionInstrument?.instrument_name &&
      selectedOptionInstrument.instrument_name !== scrolledToInstrumentName &&
      scrollableRef.current
    ) {
      // Find the ref
      let rows: ITableContentRowWithCallPut[] = [];
      let rowRefs: MutableRefObject<HTMLDivElement[]>;
      if (
        currentIndexPrice &&
        Number(selectedOptionInstrument.strike) > Number(currentIndexPrice)
      ) {
        rows = filteredRows.moreThanCurrPriceRows;
        rowRefs = higherThanSpotRowRefs;
      } else {
        rows = filteredRows.lessThanCurrPriceRows;
        rowRefs = lowerThanSpotRowRefs;
      }

      // Find index of the correct ref
      const index = rows.findIndex(
        (row) =>
          row.call.instrumentName ===
            selectedOptionInstrument.instrument_name ||
          row.put.instrumentName === selectedOptionInstrument.instrument_name
      );

      if (index && rowRefs.current[index]) {
        const rowElement = rowRefs.current[index];
        setScrolledToInstrumentName(selectedOptionInstrument.instrument_name);
        scrollVerticalTo(rowElement, true, true);
      }
    }
  }, [
    scrollVerticalTo,
    currentIndexPrice,
    filteredRows,
    selectedOptionInstrument,
    scrolledToInstrumentName,
  ]);
}
